import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import ScrollReveal, { ScrollRevealContainer } from "@/components/feature/ScrollReveal";
import { caseService, interventionService, inputService, userService, api } from "@/services/api";
import { mapDbCaseToFrontend, mapDbInterventionToFrontend } from "@/services/mapping";


const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  abierto: { label: "Abierto", color: "bg-secondary-100 text-secondary-700" },
  en_evaluacion: { label: "En Evaluación", color: "bg-yellow-100 text-yellow-700" },
  en_intervencion: { label: "En Intervención", color: "bg-primary-100 text-primary-700" },
  esperando_insumos: { label: "Esperando Insumos", color: "bg-orange-100 text-orange-700" },
  esperando_informe_docente: { label: "Esperando Informe Docente", color: "bg-orange-100 text-orange-700" },
  esperando_validacion: { label: "Esperando Validación", color: "bg-yellow-100 text-yellow-700" },
  informe_generado: { label: "Informe Generado", color: "bg-accent-100 text-accent-700" },
  enviado_tribunal: { label: "Enviado a Tribunal", color: "bg-green-100 text-green-700" },
  derivado_red_externa: { label: "Derivado a Red Externa", color: "bg-purple-100 text-purple-700" },
  en_seguimiento: { label: "En Seguimiento", color: "bg-blue-100 text-blue-700" },
  cerrado: { label: "Cerrado", color: "bg-background-200 text-foreground-500" },
  reabierto: { label: "Reabierto", color: "bg-red-100 text-red-700" },
};

const INTERVENCION_ICONS: Record<string, string> = {
  entrevista_estudiante: "ri-user-voice-line",
  entrevista_apoderado: "ri-parent-line",
  entrevista_docente: "ri-presentation-line",
  observacion_conducta: "ri-eye-line",
  derivacion_interna: "ri-arrow-right-circle-line",
  derivacion_externa: "ri-external-link-line",
  seguimiento_psicologico: "ri-brain-line",
  seguimiento_orientacion: "ri-compass-line",
  medida_formativa: "ri-book-read-line",
  medida_disciplinaria: "ri-error-warning-line",
  reunion_equipo: "ri-group-line",
  comunicacion_tribunal: "ri-scales-line",
  recepcion_oficio: "ri-mail-check-line",
  envio_informe: "ri-send-plane-line",
  contacto_red_externa: "ri-share-forward-line",
  cierre_caso: "ri-check-double-line",
  registro_clinico: "ri-brain-line",
  otro: "ri-more-line",
};

const INTERVENCION_LABELS: Record<string, string> = {
  entrevista_estudiante: "Entrevista estudiante",
  entrevista_apoderado: "Entrevista apoderado",
  entrevista_docente: "Entrevista docente",
  observacion_conducta: "Observación de conducta",
  derivacion_interna: "Derivación interna",
  derivacion_externa: "Derivación externa",
  seguimiento_psicologico: "Seguimiento psicológico",
  seguimiento_orientacion: "Seguimiento orientación",
  medida_formativa: "Medida formativa",
  medida_disciplinaria: "Medida disciplinaria",
  reunion_equipo: "Reunión de equipo",
  comunicacion_tribunal: "Comunicación tribunal",
  recepcion_oficio: "Recepción oficio",
  envio_informe: "Envío informe",
  contacto_red_externa: "Contacto red externa",
  cierre_caso: "Cierre de caso",
  registro_clinico: "Registro clínico",
  otro: "Otro",
};

type TabKey = "intervenciones" | "documentos" | "tribunal" | "informes" | "insumos";

export default function CasoDetallePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>("intervenciones");
  const [showRegistrarIntervencion, setShowRegistrarIntervencion] = useState(false);
  const [showSolicitarInsumo, setShowSolicitarInsumo] = useState(false);
  const [showGenerarInforme, setShowGenerarInforme] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [caso, setCaso] = useState<any>(null);
  const [intervenciones, setIntervenciones] = useState<any[]>([]);
  const [courtRequests, setCourtRequests] = useState<any[]>([]);
  const [teacherInputs, setTeacherInputs] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);

  useEffect(() => {
    async function loadCaseData() {
      if (!caseId) return;
      try {
        setError("");
        const [caseData, timelineData, judicialData, inputsData] = await Promise.all([
          caseService.get(caseId),
          caseService.getTimeline(caseId),
          api.get(`/judicial`).then(res => res.data),
          api.get(`/insumos/solicitados`).then(res => res.data)
        ]);

        if (caseData) {
          setCaso(mapDbCaseToFrontend(caseData));
        }

        if (Array.isArray(timelineData)) {
          setIntervenciones(timelineData.map(mapDbInterventionToFrontend));
        }

        if (Array.isArray(judicialData)) {
          setCourtRequests(judicialData.filter(d => d.caso_id === caseId).map(dbJud => ({
            id: dbJud.id,
            caseId: dbJud.caso_id,
            tribunal: dbJud.tribunal,
            rit: dbJud.rit_ruc,
            fechaVencimiento: dbJud.fecha_vencimiento ? new Date(dbJud.fecha_vencimiento).toISOString().slice(0, 10) : "",
            fechaLimite: dbJud.fecha_vencimiento ? new Date(dbJud.fecha_vencimiento).toISOString().slice(0, 10) : "",
            estado: dbJud.estado || "pendiente",
            tipoInforme: dbJud.tipo_requerimiento || "informe_social"
          })));
        }

        if (Array.isArray(inputsData)) {
          setTeacherInputs(inputsData.filter(d => d.caso_id === caseId).map(dbInput => ({
            id: dbInput.id,
            caseId: dbInput.caso_id,
            docenteName: dbInput.docente_nombre || "Docente",
            studentName: dbInput.estudiante_nombre || "Estudiante",
            materia: "Asignatura",
            fechaLimite: dbInput.fecha_limite ? new Date(dbInput.fecha_limite).toISOString().slice(0, 10) : "",
            estado: dbInput.estado || "pendiente",
            respuesta: dbInput.respuesta || "",
            instrucciones: dbInput.instrucciones || ""
          })));
        }

        // Reports from docs service
        const docsData = await api.get("/documentos/institucionales").then(res => res.data).catch(() => []);
        if (Array.isArray(docsData)) {
          setReports(docsData);
        }
      } catch (err: any) {
        console.error("Error loading case detail from API:", err);
        setError("Error al cargar detalles del caso desde Azure: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadCaseData();
  }, [caseId, refreshTrigger]);

  if (loading) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-2" />
        <p className="text-sm text-foreground-500">Cargando detalles del caso...</p>
      </div>
    );
  }

  if (!caso) {
    return (
      <div className="p-6 text-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-background-100">
          <i className="ri-error-warning-line text-2xl text-foreground-400" />
        </div>
        <h2 className="text-lg font-semibold text-foreground-900">Caso no encontrado</h2>
        <p className="text-sm text-foreground-500 mt-1">Verifica el identificador del caso</p>
        <button onClick={() => navigate("/casos")} className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap">Volver a Casos</button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[caso.estado] || STATUS_CONFIG.abierto;

  const getSemaforo = () => {
    if (caso.estado === "esperando_insumos" && caso.prioridad === "critica") return { color: "bg-red-500", text: "Crítico - Acción inmediata", bg: "bg-red-50 border-red-200" };
    if (caso.prioridad === "critica" && caso.estado !== "cerrado") return { color: "bg-red-500", text: "Prioridad crítica", bg: "bg-red-50 border-red-200" };
    if (caso.estado === "esperando_insumos" || caso.estado === "esperando_informe_docente") return { color: "bg-yellow-400", text: "Esperando información", bg: "bg-yellow-50 border-yellow-200" };
    if (caso.estado === "informe_generado") return { color: "bg-yellow-400", text: "Informe pendiente de validación", bg: "bg-yellow-50 border-yellow-200" };
    if (caso.estado === "enviado_tribunal" || caso.estado === "cerrado") return { color: "bg-green-500", text: "Al día", bg: "bg-green-50 border-green-200" };
    return { color: "bg-green-500", text: "En proceso - Sin alertas", bg: "bg-green-50 border-green-200" };
  };

  const semaforo = getSemaforo();

  const tabs: { key: TabKey; label: string; count: number; icon: string }[] = [
    { key: "intervenciones", label: "Intervenciones", count: intervenciones.length, icon: "ri-timeline-view" },
    { key: "documentos", label: "Documentos", count: 0, icon: "ri-file-text-line" },
    { key: "tribunal", label: "Tribunal", count: courtRequests.length, icon: "ri-scales-3-line" },
    { key: "informes", label: "Informes", count: reports.length, icon: "ri-article-line" },
    { key: "insumos", label: "Solic. Docentes", count: teacherInputs.length, icon: "ri-send-plane-line" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      {/* Back button */}
      <ScrollReveal className="scroll-reveal">
        <button
          onClick={() => navigate("/casos")}
          className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
        >
          <span className="flex items-center justify-center w-4 h-4"><i className="ri-arrow-left-line text-sm" /></span>
          Volver a Casos
        </button>
      </ScrollReveal>

      {/* Header */}
      <ScrollReveal className="scroll-reveal">
        <div className={`border rounded-xl p-5 ${semaforo.bg}`}>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs font-mono bg-white/80 px-2 py-0.5 rounded text-foreground-600">{caso.folio}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                {caso.judicializado && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Judicializado</span>
                )}
                <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                  caso.prioridad === "critica" ? "text-red-600" : caso.prioridad === "alta" ? "text-orange-500" : "text-foreground-500"
                }`}>
                  <span className={`w-2 h-2 rounded-full ${semaforo.color}`} />
                  {semaforo.text}
                </span>
              </div>
              <h1 className="text-xl font-bold text-foreground-950">{caso.titulo}</h1>
              <p className="text-sm text-foreground-600 mt-1">{caso.descripcion}</p>
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-foreground-500">
                <span><strong className="text-foreground-700">Estudiante:</strong> {caso.studentName} · {caso.curso}</span>
                <span><strong className="text-foreground-700">Apertura:</strong> {caso.fechaApertura}</span>
                <span><strong className="text-foreground-700">Límite:</strong> {caso.fechaLimite}</span>
                <span><strong className="text-foreground-700">Responsable:</strong> {caso.responsableName}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 lg:flex-col">
              <button
                onClick={() => setShowRegistrarIntervencion(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-4 h-4"><i className="ri-add-line" /></span>
                Registrar Intervención
              </button>
              <button
                onClick={() => setShowSolicitarInsumo(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-secondary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-4 h-4"><i className="ri-send-plane-line" /></span>
                Solicitar Insumo
              </button>
              <button
                onClick={() => setShowGenerarInforme(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-accent-500 text-background-50 rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-4 h-4"><i className="ri-article-line" /></span>
                Generar Informe
              </button>
            </div>
          </div>
        </div>
      </ScrollReveal>

      {/* Tabs */}
      <ScrollReveal className="scroll-reveal">
        <div className="flex items-center gap-1 border-b border-background-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-foreground-500 hover:text-foreground-700"
              }`}
            >
              <span className="flex items-center justify-center w-4 h-4"><i className={tab.icon} /></span>
              {tab.label}
              {tab.count > 0 && (
                <span className="text-xs bg-background-100 px-1.5 py-0.5 rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </ScrollReveal>

      {/* Tab Content */}
      {activeTab === "intervenciones" && (
        <div>
          {intervenciones.length === 0 ? (
            <div className="text-center py-12 text-foreground-400 scroll-reveal">
              <span className="flex items-center justify-center w-12 h-12 mx-auto mb-3"><i className="ri-history-line text-3xl text-foreground-300" /></span>
              <p className="text-sm">Aún no hay intervenciones registradas</p>
              <button
                onClick={() => setShowRegistrarIntervencion(true)}
                className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap"
              >
                Registrar primera intervención
              </button>
            </div>
          ) : (
            <ScrollRevealContainer staggerDelay={100}>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-5 top-0 bottom-0 w-px bg-background-200" />
                <div className="space-y-4">
                  {intervenciones.map((interv, idx) => (
                    <div key={interv.id} className="relative pl-12 scroll-reveal" style={{ transitionDelay: `${idx * 100}ms` }}>
                      {/* Timeline dot */}
                      <div className={`absolute left-[11px] top-2 w-3.5 h-3.5 rounded-full border-2 border-white ${
                        interv.confidencial ? "bg-purple-400" : "bg-primary-500"
                      } ring-2 ${interv.confidencial ? "ring-purple-200" : "ring-primary-200"}`} />

                      <div className={`bg-white border rounded-lg p-4 ${interv.confidencial ? "border-purple-200 bg-purple-50/30" : "border-background-200"}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="flex items-center justify-center w-5 h-5 text-foreground-600">
                            <i className={`${INTERVENCION_ICONS[interv.tipoIntervencion] || "ri-more-line"} text-sm`} />
                          </span>
                          <span className="text-sm font-semibold text-foreground-900">
                            {INTERVENCION_LABELS[interv.tipoIntervencion] || interv.tipoIntervencion}
                          </span>
                          {interv.confidencial && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 font-medium">Confidencial</span>
                          )}
                          <span className="text-xs text-foreground-400 ml-auto">{interv.fechaFormatted}</span>
                        </div>

                        <p className="text-sm text-foreground-700 mb-2">{interv.descripcion}</p>

                        {interv.acuerdos && (
                          <div className="mt-2 p-3 bg-background-50 rounded-lg">
                            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Acuerdos</p>
                            <p className="text-sm text-foreground-800">{interv.acuerdos}</p>
                          </div>
                        )}

                        {interv.proximosPasos && (
                          <div className="mt-2 p-3 bg-background-50 rounded-lg">
                            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Próximos Pasos</p>
                            <p className="text-sm text-foreground-800">{interv.proximosPasos}</p>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-foreground-400">
                          <span>Registrado por: <span className="font-medium text-foreground-600">{interv.responsableName}</span></span>
                          <span>·</span>
                          <span>{interv.responsableRol}</span>
                          {interv.documentosAdjuntos.length > 0 && (
                            <>
                              <span>·</span>
                              <span className="flex items-center gap-1 text-primary-600">
                                <span className="flex items-center justify-center w-3.5 h-3.5"><i className="ri-attachment-2 text-xs" /></span>
                                {interv.documentosAdjuntos.length} adjunto{interv.documentosAdjuntos.length !== 1 ? "s" : ""}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollRevealContainer>
          )}
        </div>
      )}

      {activeTab === "documentos" && (
        <div className="text-center py-16 text-foreground-400 scroll-reveal">
          <span className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-background-100">
            <i className="ri-file-text-line text-2xl text-foreground-300" />
          </span>
          <p className="text-sm font-medium">Sin documentos adjuntos</p>
          <p className="text-xs mt-1">Los documentos y evidencias se mostrarán aquí</p>
          <button className="mt-4 px-4 py-2 border border-background-200 rounded-lg text-sm cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap">
            Adjuntar documento
          </button>
        </div>
      )}

      {activeTab === "tribunal" && (
        <div className="space-y-4">
          {courtRequests.length === 0 ? (
            <div className="text-center py-16 text-foreground-400 scroll-reveal">
              <span className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-scales-3-line text-2xl text-foreground-300" />
              </span>
              <p className="text-sm font-medium">Sin requerimientos de tribunal</p>
              <p className="text-xs mt-1">Este caso no tiene oficios o solicitudes judiciales registradas</p>
              <button className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap">
                Registrar oficio de tribunal
              </button>
            </div>
          ) : (
            courtRequests.map((cr) => (
              <div key={cr.id} className="bg-white border border-background-200 rounded-xl p-5 scroll-reveal">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600">
                    <i className="ri-scales-3-line text-sm" />
                  </span>
                  <h3 className="text-base font-semibold text-foreground-900">{cr.tribunal}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    cr.estado === "enviado" ? "bg-green-100 text-green-700"
                      : cr.estado === "vencido" ? "bg-red-100 text-red-700"
                      : cr.estado === "en_proceso" ? "bg-yellow-100 text-yellow-700"
                      : "bg-background-100 text-foreground-600"
                  }`}>
                    {cr.estado === "enviado" ? "Enviado" : cr.estado === "vencido" ? "Vencido" : cr.estado === "en_proceso" ? "En proceso" : "Pendiente"}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-foreground-400">Causa</p>
                    <p className="font-medium text-foreground-900">{cr.numeroCausa}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">Tipo Informe</p>
                    <p className="font-medium text-foreground-900 capitalize">{cr.tipoInforme}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">Fecha Recepción</p>
                    <p className="font-medium text-foreground-900">{cr.fechaRecepcion}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">Fecha Límite</p>
                    <p className={`font-medium ${cr.estado === "vencido" ? "text-red-600" : "text-foreground-900"}`}>{cr.fechaLimite}</p>
                  </div>
                </div>
                {cr.fechaEnvio && (
                  <div className="mt-3 pt-3 border-t border-background-100 flex items-center gap-2 text-sm">
                    <span className="text-green-600 flex items-center gap-1">
                      <span className="flex items-center justify-center w-4 h-4"><i className="ri-check-line text-sm" /></span>
                      Enviado el {cr.fechaEnvio}
                    </span>
                    {cr.documentoEnviado && (
                      <span className="text-foreground-400 text-xs">· {cr.documentoEnviado}</span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "informes" && (
        <div className="space-y-4">
          {reports.length === 0 ? (
            <div className="text-center py-16 text-foreground-400 scroll-reveal">
              <span className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-article-line text-2xl text-foreground-300" />
              </span>
              <p className="text-sm font-medium">Sin informes generados</p>
              <p className="text-xs mt-1">Genera informes con IA a partir de los antecedentes del caso</p>
              <button
                onClick={() => setShowGenerarInforme(true)}
                className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap"
              >
                Generar informe con IA
              </button>
            </div>
          ) : (
            reports.map((r) => (
              <div key={r.id} className="bg-white border border-background-200 rounded-xl p-5 scroll-reveal">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-foreground-900">{r.tipoReporte}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    r.estado === "enviado" ? "bg-green-100 text-green-700"
                      : r.estado === "aprobado" ? "bg-accent-100 text-accent-700"
                      : r.estado === "revisado" ? "bg-yellow-100 text-yellow-700"
                      : "bg-background-100 text-foreground-600"
                  }`}>
                    {r.estado === "enviado" ? "Enviado" : r.estado === "aprobado" ? "Aprobado" : r.estado === "revisado" ? "Revisado" : "Borrador"}
                  </span>
                </div>
                <div className="bg-background-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <pre className="text-xs text-foreground-700 font-sans whitespace-pre-wrap">{r.contenidoBorrador}</pre>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-foreground-400">
                  <span>Generado: {r.createdAt}</span>
                  {r.approvedBy && <span>· Aprobado: {r.approvedAt} por Directivo</span>}
                  {r.sentBy && <span>· Enviado: {r.sentAt} por {r.sentBy === "u4" ? "Valentina Soto" : r.sentBy}</span>}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button className="px-3 py-1.5 text-xs border border-background-200 rounded-lg cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap">Descargar Word</button>
                  <button className="px-3 py-1.5 text-xs border border-background-200 rounded-lg cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap">Descargar PDF</button>
                  <button className="px-3 py-1.5 text-xs bg-primary-500 text-white rounded-lg cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">Editar borrador</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "insumos" && (
        <div className="space-y-4">
          {teacherInputs.length === 0 ? (
            <div className="text-center py-16 text-foreground-400 scroll-reveal">
              <span className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-send-plane-line text-2xl text-foreground-300" />
              </span>
              <p className="text-sm font-medium">Sin solicitudes docentes</p>
              <p className="text-xs mt-1">Solicita información a profesores para informes o seguimiento</p>
              <button
                onClick={() => setShowSolicitarInsumo(true)}
                className="mt-4 px-4 py-2 bg-secondary-500 text-white rounded-lg text-sm cursor-pointer whitespace-nowrap"
              >
                Solicitar insumo
              </button>
            </div>
          ) : (
            teacherInputs.map((ti) => (
              <div key={ti.id} className="bg-white border border-background-200 rounded-xl p-5 scroll-reveal">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-user-received-2-line text-sm" />
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-900">{ti.docenteName}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    ti.estado === "aprobado" || ti.estado === "entregado" ? "bg-green-100 text-green-700"
                      : ti.estado === "vencido" ? "bg-red-100 text-red-700"
                      : ti.estado === "devuelto_correccion" ? "bg-yellow-100 text-yellow-700"
                      : "bg-background-100 text-foreground-600"
                  }`}>
                    {ti.estado === "aprobado" ? "Aprobado" : ti.estado === "entregado" ? "Entregado" : ti.estado === "vencido" ? "Vencido" : ti.estado === "devuelto_correccion" ? "Devuelto" : ti.estado === "solicitado" ? "Solicitado" : "Pendiente"}
                  </span>
                </div>
                <p className="text-xs text-foreground-500 mb-2">Tipo: {ti.tipoInsumo} · Límite: {ti.fechaLimite}</p>
                <p className="text-sm text-foreground-700 bg-background-50 rounded-lg p-3">{ti.descripcionSolicitud}</p>
                {ti.respuesta && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs font-semibold text-green-700 mb-1">Respuesta del docente:</p>
                    <p className="text-sm text-foreground-800">{ti.respuesta}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Registrar Intervención Modal */}
      {showRegistrarIntervencion && (
        <RegistrarIntervencionModal
          onClose={() => setShowRegistrarIntervencion(false)}
          caseId={caso.id}
          onSuccess={triggerRefresh}
        />
      )}

      {/* Solicitar Insumo Modal */}
      {showSolicitarInsumo && (
        <SolicitarInsumoModal
          onClose={() => setShowSolicitarInsumo(false)}
          caseId={caso.id}
          studentName={caso.studentName}
          curso={caso.curso}
          onSuccess={triggerRefresh}
        />
      )}

      {/* Generar Informe Modal */}
      {showGenerarInforme && (
        <GenerarInformeModal
          onClose={() => setShowGenerarInforme(false)}
          caseId={caso.id}
          studentName={caso.studentName}
          intervenciones={intervenciones}
          teacherInputs={teacherInputs}
        />
      )}
    </div>
  );
}

// ── Sub-components ──

function RegistrarIntervencionModal({ onClose, caseId, onSuccess }: { onClose: () => void; caseId: string; onSuccess: () => void }) {
  const [tipo, setTipo] = useState("entrevista_estudiante");
  const [descripcion, setDescripcion] = useState("");
  const [acuerdos, setAcuerdos] = useState("");
  const [proximosPasos, setProximosPasos] = useState("");
  const [confidencial, setConfidencial] = useState(false);
  const [loading, setLoading] = useState(false);

  const tipos = Object.entries(INTERVENCION_LABELS);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!descripcion.trim()) {
      alert("Por favor ingresa una descripción para la intervención.");
      return;
    }
    const todayStr = new Date().toISOString().slice(0, 10);
    const payload = {
      caso_id: caseId,
      tipo,
      descripcion,
      fecha: todayStr,
      participantes: [],
      acuerdos: acuerdos.trim() || null,
      proximos_pasos: proximosPasos.trim() || null,
      confidencial
    };
    try {
      setLoading(true);
      await interventionService.create(payload);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      alert("Error al registrar intervención: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <h2 className="text-lg font-semibold text-foreground-950">Registrar Intervención</h2>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo de Intervención</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {tipos.map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Descripción</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={4}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              placeholder="Describe la intervención realizada..."
              maxLength={500}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Acuerdos</label>
            <textarea
              value={acuerdos}
              onChange={(e) => setAcuerdos(e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              placeholder="Acuerdos alcanzados..."
              maxLength={500}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Próximos Pasos</label>
            <textarea
              value={proximosPasos}
              onChange={(e) => setProximosPasos(e.target.value)}
              rows={2}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              placeholder="Qué se hará a continuación..."
              maxLength={500}
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex cursor-pointer items-center">
              <input type="checkbox" checked={confidencial} onChange={(e) => setConfidencial(e.target.checked)} className="sr-only peer" />
              <div className="w-9 h-5 bg-background-200 rounded-full peer-checked:bg-purple-500 after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </label>
            <span className="text-sm text-foreground-700">Registro confidencial</span>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={loading} className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1.5">
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm" /> Guardando...
                </>
              ) : (
                "Guardar Intervención"
              )}
            </button>
            <button type="button" disabled={loading} onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function SolicitarInsumoModal({ onClose, caseId, studentName, curso, onSuccess }: { onClose: () => void; caseId: string; studentName: string; curso: string; onSuccess: () => void }) {
  const [docente, setDocente] = useState("");
  const [tipoInsumo, setTipoInsumo] = useState("Informe de rendimiento y comportamiento");
  const [descripcion, setDescripcion] = useState("");
  const [fechaLimite, setFechaLimite] = useState("");
  const [docentes, setDocentes] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadDocentes() {
      try {
        setLoadingUsers(true);
        const data = await userService.list();
        const filtered = data.filter((u: any) => u.rol === "docente" || u.rol === "profesor_jefe");
        setDocentes(filtered);
        if (filtered.length > 0) {
          setDocente(filtered[0].id);
        }
      } catch (err) {
        console.error("Error loading teachers:", err);
      } finally {
        setLoadingUsers(false);
      }
    }
    loadDocentes();

    // Default due date to 7 days from now
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 7);
    setFechaLimite(futureDate.toISOString().slice(0, 10));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docente) {
      alert("Por favor selecciona un docente.");
      return;
    }
    if (!fechaLimite) {
      alert("Por favor selecciona una fecha límite.");
      return;
    }
    const payload = {
      caso_id: caseId,
      docente_id: docente,
      tipo_insumo: tipoInsumo,
      instrucciones: descripcion.trim() || null,
      plazo_entrega: fechaLimite
    };
    try {
      setLoading(true);
      await inputService.createSolicitud(payload);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      alert("Error al solicitar insumo: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <h2 className="text-lg font-semibold text-foreground-950">Solicitar Insumo a Docente</h2>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-background-50 rounded-lg p-3 text-sm text-foreground-600">
            Estudiante: <strong className="text-foreground-900">{studentName}</strong> · {curso}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Docente</label>
            <select
              value={docente}
              onChange={(e) => setDocente(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {loadingUsers ? (
                <option value="">Cargando docentes...</option>
              ) : docentes.length === 0 ? (
                <option value="">No hay docentes registrados</option>
              ) : (
                docentes.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nombre} ({d.rol === "profesor_jefe" ? "Profesor Jefe" : "Docente"})
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo de Insumo</label>
            <input
              type="text"
              value={tipoInsumo}
              onChange={(e) => setTipoInsumo(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Descripción de la solicitud</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              placeholder="Describe qué información necesitas del docente..."
              maxLength={500}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Fecha Límite</label>
            <input
              type="date"
              value={fechaLimite}
              onChange={(e) => setFechaLimite(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={loading} className="px-5 py-2.5 bg-secondary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1.5">
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm" /> Enviando...
                </>
              ) : (
                "Enviar Solicitud"
              )}
            </button>
            <button type="button" disabled={loading} onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function GenerarInformeModal({ onClose, caseId, studentName, intervenciones, teacherInputs }: { onClose: () => void; caseId: string; studentName: string; intervenciones: any[]; teacherInputs: any[] }) {
  const [tipoInforme, setTipoInforme] = useState("integral");
  const [generando, setGenerando] = useState(false);
  const [generado, setGenerado] = useState(false);
  const [borradorText, setBorradorText] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const tiposInforme = [
    { value: "integral", label: "Informe Integral" },
    { value: "comportamiento", label: "Informe de Comportamiento" },
    { value: "rendimiento", label: "Informe de Rendimiento" },
    { value: "asistencia", label: "Informe de Asistencia" },
    { value: "convivencia", label: "Informe de Convivencia Escolar" },
    { value: "orientacion", label: "Informe de Orientación" },
    { value: "compromiso_apoderado", label: "Informe Compromiso Apoderado" },
    { value: "psicoeducativo", label: "Informe Psicoeducativo" },
  ];

  const handleGenerar = () => {
    setGenerando(true);
    setTimeout(() => {
      setGenerando(false);
      setGenerado(true);
      setBorradorText(getBorradorText());
    }, 1500);
  };

  const getBorradorText = () => {
    const academicos = teacherInputs
      .filter((ti) => {
        const t = ti.tipoInsumo.toLowerCase();
        return t.includes("rendimiento") || t.includes("acadé") || t.includes("nota") || t.includes("insumo");
      })
      .map((ti) => `Docente: ${ti.docenteName}\n- Detalle solicitado: ${ti.instrucciones || "Ninguno"}\n- Respuesta: ${ti.respuesta || "Pendiente de respuesta"}`)
      .join("\n\n");

    const asistencias = teacherInputs
      .filter((ti) => ti.tipoInsumo.toLowerCase().includes("asistencia"))
      .map((ti) => `Docente: ${ti.docenteName}\n- Detalle solicitado: ${ti.instrucciones || "Ninguno"}\n- Respuesta: ${ti.respuesta || "Pendiente de respuesta"}`)
      .join("\n\n");

    const comportamientos = teacherInputs
      .filter((ti) => {
        const t = ti.tipoInsumo.toLowerCase();
        return t.includes("comportamiento") || t.includes("conducta") || t.includes("convivencia") || t.includes("disciplina");
      })
      .map((ti) => `Docente: ${ti.docenteName}\n- Detalle solicitado: ${ti.instrucciones || "Ninguno"}\n- Respuesta: ${ti.respuesta || "Pendiente de respuesta"}`)
      .join("\n\n");

    const apoderados = teacherInputs
      .filter((ti) => ti.tipoInsumo.toLowerCase().includes("apoderado") || ti.tipoInsumo.toLowerCase().includes("familia"))
      .map((ti) => `Docente: ${ti.docenteName}\n- Detalle solicitado: ${ti.instrucciones || "Ninguno"}\n- Respuesta: ${ti.respuesta || "Pendiente de respuesta"}`)
      .join("\n\n");

    const intervencionesList = intervenciones
      .map((i, idx) => {
        const dateStr = i.fecha ? new Date(i.fecha).toLocaleDateString("es-CL") : "Sin fecha";
        const tipoLabel = INTERVENCION_LABELS[i.tipo] || i.tipo;
        let txt = `[${dateStr}] ${tipoLabel}\n   Descripción: ${i.descripcion}`;
        if (i.acuerdos) txt += `\n   Acuerdos: ${i.acuerdos}`;
        if (i.proximosPasos) txt += `\n   Próximos pasos: ${i.proximosPasos}`;
        return txt;
      })
      .join("\n\n");

    const labelReporte = tiposInforme.find((t) => t.value === tipoInforme)?.label.toUpperCase() || "INFORME INTEGRAL";

    return `INFORME ESCOLAR - ${labelReporte}

I. IDENTIFICACIÓN DEL ESTUDIANTE
Nombre: ${studentName}
Establecimiento: Liceo Valentina Tereshkova

II. ANTECEDENTES RECOPILADOS
A continuación se detallan los insumos provistos por el equipo docente:

II.1 Rendimiento académico:
${academicos || "[No se registran reportes académicos específicos para este caso en la plataforma. Se sugiere complementar con el promedio general actual.]"}

II.2 Asistencia:
${asistencias || "[No se registran bitácoras de asistencia específicas para este caso en la plataforma. Se sugiere complementar con porcentaje de asistencia actual.]"}

II.3 Comportamiento y convivencia escolar:
${comportamientos || "[No se registran incidentes adicionales reportados en los libros de clases.]"}

II.4 Compromiso del apoderado:
${apoderados || "[No se registran reportes de reuniones o citaciones al apoderado en este módulo.]"}

III. INTERVENCIONES REALIZADAS
Bitácora de intervenciones y entrevistas ejecutadas por los profesionales del establecimiento:

${intervencionesList || "[No se registran intervenciones en la plataforma para este estudiante.]"}

IV. CONCLUSIÓN Y RECOMENDACIONES
Basado en los antecedentes expuestos:
- El caso se encuentra actualmente en estado de seguimiento activo.
- Se recomienda continuar el monitoreo de los compromisos adquiridos en las intervenciones y las respuestas a insumos docentes.
- Este reporte ha sido generado de manera automática y objetiva a partir de la información fidedigna guardada en la plataforma, sin emitir juicios de valor ni diagnósticos clínicos externos.

---
*Borrador generado automáticamente por Ezer Convivencia360. Requiere validación y edición de un profesional competente.*
*Este informe se basa exclusivamente en la información registrada en la plataforma.*`;
  };

  const handleDescargarWord = () => {
    const element = document.createElement("a");
    const file = new Blob([borradorText], { type: 'application/msword;charset=utf-8' });
    element.href = URL.createObjectURL(file);
    element.download = `Informe_${studentName.replace(/\s+/g, "_")}.doc`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDescargarPDF = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Informe - ${studentName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; max-width: 800px; margin: 0 auto; }
            h1 { text-align: center; color: #111; font-size: 22px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            pre { white-space: pre-wrap; font-family: inherit; font-size: 14px; margin-top: 20px; }
            .no-print {
              background: #ecfdf5;
              padding: 14px;
              border-radius: 12px;
              margin-bottom: 25px;
              text-align: center;
              font-size: 14px;
              border: 1px solid #a7f3d0;
              color: #065f46;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 12px;
            }
            .print-btn {
              background: #10b981;
              color: white;
              border: none;
              padding: 8px 16px;
              border-radius: 8px;
              font-weight: bold;
              cursor: pointer;
              transition: background 0.2s;
            }
            .print-btn:hover {
              background: #059669;
            }
            @media print {
              .no-print { display: none; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="no-print">
            <span>Para guardar este informe como archivo PDF, selecciona la opción <strong>"Guardar como PDF"</strong> en el cuadro de diálogo de impresión.</span>
            <button class="print-btn" onclick="window.print()">Imprimir / Guardar PDF</button>
          </div>
          <h1>INFORME INTEGRAL DE CONVIVENCIA Y RENDIMIENTO</h1>
          <pre>${borradorText}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <h2 className="text-lg font-semibold text-foreground-950">Generar Informe con IA</h2>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {!generado ? (
            <>
              <div className="bg-background-50 rounded-lg p-3 text-sm text-foreground-600">
                La IA usará solo la información registrada en este caso para generar el borrador del informe. No inventará antecedentes ni emitirá diagnósticos clínicos.
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo de Informe</label>
                <select
                  value={tipoInforme}
                  onChange={(e) => setTipoInforme(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                >
                  {tiposInforme.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-700">
                <strong>Importante:</strong> El informe se generará en estado "borrador". Deberá ser revisado, editado y validado por un profesional antes de su envío o descarga.
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleGenerar}
                  disabled={generando}
                  className="px-5 py-2.5 bg-accent-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {generando ? (
                    <span className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-4 h-4 animate-spin"><i className="ri-loader-4-line" /></span>
                      Generando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-4 h-4"><i className="ri-magic-line" /></span>
                      Generar Borrador con IA
                    </span>
                  )}
                </button>
                <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">
                  Cancelar
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <span className="flex items-center justify-center w-5 h-5"><i className="ri-check-line" /></span>
                Borrador generado exitosamente. Revisa y edita antes de aprobar.
              </div>
              <div className="bg-background-50 rounded-lg p-4 max-h-80 overflow-y-auto border border-background-200">
                {isEditing ? (
                  <textarea
                    value={borradorText}
                    onChange={(e) => setBorradorText(e.target.value)}
                    rows={12}
                    className="w-full text-xs text-foreground-800 font-mono bg-white p-3 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                ) : (
                  <pre className="text-xs text-foreground-700 font-sans whitespace-pre-wrap">{borradorText}</pre>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  {isEditing ? "Guardar Borrador" : "Editar Borrador"}
                </button>
                <button onClick={handleDescargarWord} className="px-4 py-2 border border-background-200 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-background-50">Descargar Word</button>
                <button onClick={handleDescargarPDF} className="px-4 py-2 border border-background-200 rounded-lg text-sm cursor-pointer whitespace-nowrap hover:bg-background-50">Descargar PDF</button>
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground-500 cursor-pointer whitespace-nowrap hover:text-foreground-700">Cerrar</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}